import type { BenefitClaim, BenefitType } from "../shared/types";

/** Names and descriptions are the approved publishing copy; API codes stay unchanged. */
export const benefitCategories = [
  { code: "RENT_FRAUD", name: "월세 사기 피해", description: "전·월세 보증금 사기로 인한 피해", icon: "🏚️", tone: "rent" },
  { code: "INSURANCE_LAPSE", name: "보험 실효 피해", description: "보험 실효로 발생한 손해", icon: "📉", tone: "insurance" },
  { code: "OTHER_DAMAGE", name: "기타 피해", description: "그 외 심사를 통해 지원 가능한 피해", icon: "💬", tone: "other" },
] as const;

export function benefitCategory(code: string, types: readonly BenefitType[] = []) {
  return benefitCategories.find((item) => item.code === code)
    ?? { code, name: types.find((item) => item.code === code)?.name ?? "기타 지원", description: types.find((item) => item.code === code)?.description ?? "", icon: "🎁" as const, tone: "other" };
}

export function benefitDateBounds(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = (type: string) => parts.find((value) => value.type === type)!.value;
  const today = `${part("year")}-${part("month")}-${part("day")}`;
  // UTC is used only to add calendar days to an already resolved Seoul date.
  const prior = new Date(`${today}T00:00:00Z`);
  prior.setUTCDate(prior.getUTCDate() - 30);
  return { today, minimum: prior.toISOString().slice(0, 10) };
}

export function benefitDateError(value: string, bounds = benefitDateBounds()): string | undefined {
  if (!value) return undefined;
  if (value > bounds.today) return "미래 날짜는 선택할 수 없어요.";
  if (value < bounds.minimum) return "사고 발생일로부터 30일이 지나 신청할 수 없어요.";
  return undefined;
}

export function benefitStage(status: string): number {
  if (status === "PAID") return 3;
  if (status === "APPROVED") return 2;
  if (["APPROVAL_PENDING", "UNDER_REVIEW", "REVIEWING", "IN_PROGRESS"].includes(status)) return 1;
  if (["RECEIVED", "SUBMITTED"].includes(status)) return 1;
  return -1;
}

export function benefitOutcome(claim: BenefitClaim) {
  if (claim.status === "PAID") return { title: "지원금 지급이 완료됐어요", description: "지급 내역과 실제 입금 내역을 확인해 주세요." };
  if (claim.status === "APPROVED") return { title: "지원금 지급이 결정됐어요", description: "심사가 완료되었어요. 지원금은 지급 준비 중이에요." };
  if (claim.status === "REJECTED") return { title: "지원금 신청이 반려되었어요", description: "자세한 심사 사유는 1:1 문의로 확인해 주세요." };
  if (benefitStage(claim.status) >= 0) return { title: "지원금 신청을 확인하고 있어요", description: "접수한 피해 내용과 자료를 바탕으로 심사가 진행돼요." };
  return { title: "신청 상태를 확인해 주세요", description: "현재 처리 상태는 1:1 문의로 확인할 수 있어요." };
}

/** Commas are display-only; preserve the fractional value for the approved won rounding policy. */
export function benefitAmountDisplay(value: string) {
  const [integer = "", fraction] = value.split(".");
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (fraction === undefined ? "" : `.${fraction}`);
}

export function benefitStatusLabel(status: string) {
  return benefitStage(status) === 1 ? "심사중" : status;
}
